import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { Stack, Typography } from '@mui/material';
import { Browser } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import useBrowser from 'hooks/use-browser';
import { useUserData } from 'hooks/use-user-data';
import Logger from 'logger';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import StepActions from './step-actions';
import StepHint from './step-hint';
import StepLayout from './step-layout';

interface Props {
  userName: string;
  password: string;

  handleUserNameChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handlePasswordChange(event: React.ChangeEvent<HTMLInputElement>): void;

  /** Zurück zu Produkt und Sitz. Fehlt, wo dieser Schritt der erste ist. */
  onBack?(): void;

  onNext(): void;
}

/**
 * Schritt 2: die LinkedIn-Zugangsdaten.
 *
 * Der Schritt legt nichts mehr an — angelegt wird erst am Ende des Standort-Schrittes,
 * wenn auch die feste IP feststeht. Was er dafür leistet: er sagt vorher, dass gleich
 * das Handy gebraucht wird. Diese eine Zeile fehlte, und deshalb lief die 2FA-Frist
 * regelmäßig ab, während niemand hinsah.
 */
const EnterAccountInfo: React.FC<Props> = ({ userName, password, handleUserNameChange, handlePasswordChange, onBack, onNext }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { language } = useUserData();

  const { userBrowser, hasPwManagerLink } = useBrowser();
  const [copied, setCopied] = useState(false);

  const getPasswordManagerUrl = () => {
    switch (userBrowser) {
      case Browser.MS_EDGE_CHROMIUM:
        return 'edge://wallet/passwords?search=linkedin&filter=linkedin';
      case Browser.CHROME:
        return 'chrome://password-manager/passwords?q=linkedin';
      case Browser.FIREFOX:
        return 'about:logins';
      case Browser.SAFARI:
        return `https://support.apple.com/${language === 'de' ? 'de-de' : 'en-us'}/105115`;
      default:
        return undefined;
    }
  };

  const copyUrlToClipboard = async () => {
    const url = getPasswordManagerUrl();
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 10 * 1000);
    } catch (e) {
      // fallback for outlook in IFrame
      // for more information see here: https://web.dev/async-clipboard/#permissions-policy-integration
      Logger.error(e);
    }
  };

  return (
    <StepLayout
      title={t('appSyncCredentialsTitle', 'Zugangsdaten von LinkedIn')}
      description={t('appSyncCredentialsDescription', 'Wir speichern sie verschlüsselt und nutzen sie nur für die Anmeldung in der App.')}
      footer={
        <StepActions
          primaryText={t('next')}
          primaryDisabled={!userName || !password}
          backText={onBack ? t('back') : undefined}
          onBack={onBack}
          onPrimary={onNext}
        />
      }
    >
      <Stack gap={2.25}>
        <Stack gap={0.75}>
          <Typography className={classes.fieldLabel}>{t('email')}</Typography>
          <PaiTextField
            value={userName}
            placeholder={`${t('email')} (LinkedIn)`}
            autocomplete={'one-time-code'}
            name={'linkedin-email'}
            onChange={handleUserNameChange}
          />
        </Stack>
        <Stack gap={0.75}>
          <Typography className={classes.fieldLabel}>{t('password')}</Typography>
          <PaiTextField
            value={password}
            placeholder={`${t('password')} (LinkedIn)`}
            autocomplete={'new-password'}
            name={'linkedin-password'}
            type={'password'}
            onChange={handlePasswordChange}
          />
          {hasPwManagerLink ? (
            <Stack direction={'row'} justifyContent={'flex-end'}>
              <PaiTooltip title={t('pwmLinkCopiedMessage')} open={copied} placement={'bottom'}>
                <span>
                  <PaiLink text={t('forgotpw')} size={'md'} variant={'default'} underline={'always'} action={copyUrlToClipboard} />
                </span>
              </PaiTooltip>
            </Stack>
          ) : null}
        </Stack>
        <StepHint
          icon={<PhoneIphoneIcon />}
          text={t(
            'appSyncCredentialsPhoneHint',
            'Gleich brauchst du kurz dein Handy: LinkedIn schickt eine Bestätigung, die du einmalig freigeben musst.',
          )}
        />
      </Stack>
    </StepLayout>
  );
};

const useStyles = makeStyles()((theme) => ({
  fieldLabel: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    color: theme.palette.greys.medium,
  },
}));

export default EnterAccountInfo;
